/* eslint-disable no-undef */
import App from "./components/App";
import { AppContainer } from "react-hot-loader";
import { initializeIcons } from "@fluentui/font-icons-mdl2";
import * as React from "react";
import * as ReactDOM from "react-dom";
import { Provider } from "react-redux";
import { RootState, store } from "./redux/store/store";
import { classicTheme } from "./styles/fluent/themes";
import { IntlProvider } from "react-intl";
import { getLanguage } from "co-addin-captions";
import { setClientId, setDocumentTenantId } from "./redux/feature/settings/configurations-slice";
import { DocPropKeys, retrieveDocumentProp } from "./utils/helpers/CustomXmlHelper";
import "./styles/base.css";
import "./styles/colors.css";
import "./styles/typography.css";
import "./styles/kendo.css";
import { navigate } from "./redux/feature/general/navigation-slice";
import LoadingManager from "./common/loading/LoadingManager";
import { FluentProvider } from "@fluentui/react-provider";
import { UserFunction } from "./api/models/user-function";
import { UserFunctionEnum } from "./api/models/user-function-enum";
import { openSeahorseLink } from "./pages/seahorse/SeahorseUtils";
import { isNotAuthorizedUser } from "./utils/user-function/userFunctionUtils";
import { Theme } from "@fluentui/react-components";
import { COPY_CUT_PASTE_PANEL_MESSAGE } from "./utils/constants/navigation";
import { clearPanelMessage } from "./redux/feature/panelMessage/panelMessage-slice";
import { actionCopyContentControl, actionCutContentControl, actionPasteContentControl } from "./office/DocumentManager";

/* global document, Office, module, require */

initializeIcons();

let isOfficeInitialized = false;
const title = "COLLABORATIVE OFFICE - TAGETIK";
let isPanelOpened = false;

const render = (Component) => {
  ReactDOM.render(
    // @ts-ignore
    <AppContainer>
      <FluentProvider theme={classicTheme as Partial<Theme>}>
        <Provider store={store}>
          <IntlProvider locale={Office.context.displayLanguage} messages={getLanguage(Office.context.displayLanguage)}>
            <Component title={title} isOfficeInitialized={isOfficeInitialized} />
          </IntlProvider>
        </Provider>
      </FluentProvider>
    </AppContainer>,
    document.getElementById("container")
  );
};

/* Render application after Office initializes */
Office.onReady(async () => {
  Office.addin.onVisibilityModeChanged(function (args) {
    if (args.visibilityMode === "Taskpane") {
      isPanelOpened = true;
    } else if (args.visibilityMode === "Hidden") {
      isPanelOpened = false;
    }
  });

  const clientType = Office.context.requirements.isSetSupported("WordApiOnline", "1.1") ? "WEB" : "APP";
  store.dispatch(setClientId(clientType));

  await retrieveDocumentProp(DocPropKeys.TenantId)
    .then((value) => store.dispatch(setDocumentTenantId(value)))
    // eslint-disable-next-line no-undef
    .catch((reason) => console.warn("Cannot retrieve Document Tenant id from here: {}", reason));

  isOfficeInitialized = true;
  render(App);
});

if ((module as any).hot) {
  (module as any).hot.accept("./components/App", () => {
    const NextApp = require("./components/App").default;
    render(NextApp);
  });
}

/*
    COMMAND FUNCTIONS
*/

export async function openPanel(event) {
  if (!isPanelOpened) {
    Office.addin.showAsTaskpane().then(() => {
      isPanelOpened = true;
      dispatchEventOpen(event);
    });
  } else {
    dispatchEventOpen(event);
  }
}

export async function customOpenPanel() {
  if (!isPanelOpened) {
    Office.addin.showAsTaskpane().then(() => {
      isPanelOpened = true;
    });
  }
}

export async function closeCopyCutPasteMessagePanel() {
  const page = store.getState().navigation.page;
  if (isPanelOpened && page === COPY_CUT_PASTE_PANEL_MESSAGE) {
    await closePanel();
    store.dispatch(clearPanelMessage());
  }
}

export async function openPage(event) {
  getSeahorseLink();
  event.completed();
}

export function getSeahorseLink(): void {
  const state: RootState = store.getState();
  let selectedConfiguration = state.configurationState.selected;
  let userFunctions: UserFunction[] = state.configurationState.configurations.find(
    (value) => value.name === selectedConfiguration
  )?.cpmUser?.userFunctions;
  let notAuthorizedUserForIXBRL =
    isNotAuthorizedUser(UserFunctionEnum.MENU_IXBRL_ADMIN, userFunctions) &&
    isNotAuthorizedUser(UserFunctionEnum.MENU_IXBRL_USER, userFunctions);
  if (notAuthorizedUserForIXBRL) {
    LoadingManager.removeAll();
    store.dispatch(navigate("IXBRL"));
  } else {
    openSeahorseLink();
  }
}

export async function closePanel() {
  if (isPanelOpened) {
    Office.addin.hide().then(() => {
      isPanelOpened = false;
    });
  }
}

export function dispatchEventOpen(event) {
  LoadingManager.removeAll();
  store.dispatch(navigate(event.source.id));
  event.completed();
}

function getGlobal() {
  // eslint-disable-next-line no-undef
  if (typeof self !== "undefined") return self;
  // eslint-disable-next-line no-undef
  if (typeof window !== "undefined") return window;
  // eslint-disable-next-line no-undef
  if (typeof global !== "undefined") return global;
  return undefined;
}

const g = getGlobal() as any;
g.openPanel = openPanel;
g.openPage = openPage;

/* https://devblogs.microsoft.com/microsoft365dev/announcing-changes-to-office-add-ins-executefunction-commands/ */
Office.actions.associate("openPanel", (e) => {
  openPanel(e);
});
Office.actions.associate("openPage", (e) => {
  openPage(e);
});

/**
 * Word Object Management Actions: Cut, Copy, Paste
 */
Office.actions.associate("copyContentControl", (event) => {
  actionCopyContentControl(event);
});
Office.actions.associate("cutContentControl", (event) => {
  actionCutContentControl(event);
});
Office.actions.associate("pasteContentControl", (event) => {
  actionPasteContentControl(event);
});
